// @flow
import React, { Component, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { withHelmet } from 'infrastructure/seo/Helmet';

import type { JobDetailType } from 'types/JobDetail';
import type { Status } from 'types/Status';
import type { ContactFormType } from 'types/ContactForm';
import type { FullUserType } from 'types/User';

import { handleTagEvent }from 'utils/tagManagerUtils';

import { STATUS_LOADING, STATUS_DEFAULT, STATUS_UNKNOWN } from 'constants/statusConstants';
import Loading from 'components/fragments/Loading';
import PageNotFound from 'components/app/PageNotFound';
import ContentBloc from 'components/newsletter/blocs/ContentBloc';
import Breadcrumb from 'components/fragments/Breadcrumb';
import JobDetailForm from 'components/job/jobDetail/JobDetailForm';

type OwnProps = {
  match: {
    params: {
      slug: string,
      one: string,
      two: string,
      three: string,
    }
  }
}

export type StateProps = {
  title: string,
  description: string,
  details: JobDetailType,
  status: Status,
  contactFormList: Array<ContactFormType>,
  userPref: FullUserType
};

export type DispatchProps = {
  onSubmit: (formInputs: Object, id: number) => void,
  fetchJobDetail: (slug: string) => void,
  fetchContactForm: () => void,
};

type Props = DispatchProps & OwnProps &
  StateProps;

class JobDetail extends Component<Props> {
  componentDidMount() {
    const { fetchJobDetail, fetchContactForm, match: { params: { slug } } } = this.props;

    fetchJobDetail(slug);
    fetchContactForm();
  }

  renderDetail = () => {
    const { content } = this.props.details;
    return content.map((blocFigure, index) => {
      return <ContentBloc key={index} {...blocFigure} />;
    });
  };

  _getJobForm = () => {
    const { contactFormList } = this.props;

    if (contactFormList && contactFormList.length > 0) {
      const jobForm = contactFormList.filter((form) => form.title === 'postuler');

      if (jobForm && jobForm.length > 0) {
        return jobForm[0].id;
      }
    }

    return 0;
  }

  render() {
    const { title, description, status, details, match : { params }, userPref, onSubmit } = this.props;
    const { id } = details;
    const splittedDocName = details.featured_pdf.split('/');
    const documentName = splittedDocName[splittedDocName.length -1];
    const backUrl = `/${params.one}/${params.two}/${params.three}`;
    if (status === STATUS_LOADING || status === STATUS_DEFAULT) {
      return <Loading />;
    }

    if (status === STATUS_UNKNOWN) {
      return <PageNotFound noMargin />;
    }

    const formId = this._getJobForm();

    return (
      <Fragment>
        <section className="banner">
          <div className="banner__thumbnail img-blue-dark" />
          <div className="container">
            <Breadcrumb
              elements={[
                { name: 'accueil', to: '/' },
                { name: 'ffr', to: `/${params.one}` },
                { name: 'carrière', to: backUrl },
                details.title
              ]}
              isDark
            />
            <h1 className="ft-h2">{title}</h1>
            <p className="banner__desc">{ description }</p>
          </div>
        </section>
        <div className="container">
          <div className="row">
            <div className="col col-md-8 col-lg-9 bg-overflow">
              <section>
              <div className="row">
                <div className="col col-lg-7on9 page">
                  <p className="clearfix">
                    <Link to={backUrl} title="Retour aux offres" className="link-chevron link-chevron--left">
                      <span>Retour aux offres</span><i className="icon icon-chevron-left"></i>
                    </Link>
                  </p>
                  <hr className="separator mt-2 mb-2 mb-md-5" />
                  <div className="ft-h6 ft-up ft-black-secondary mb-1">{ details.structure.name }</div>
                  <h2>{details.title}</h2>
                  <ul className="list-type ft-h6 ft-up mt-2">
                    { details.jobtype && details.jobtype !== '' &&
                    <li> <i className="icon icon-case is-inline"></i>{details.jobtype}</li> }
                    { details.city && details.city !== '' &&
                    <li> <i className="icon icon-place is-inline"></i>{details.city}</li> }
                    {
                      details.structure.website &&
                      details.structure.website !== '' && (
                      <li>
                        <i className="icon icon-site is-inline"></i>
                        <a href={ details.structure.website } title="Voir le site du club">Voir le site du club</a>
                      </li>
                    )}
                    { details.type_contrat && details.type_contrat !== '' &&
                    <li> <i className="icon icon-jersey is-inline"></i>{details.type_contrat}</li> }
                    { details.date_cloture && details.date_cloture !== '' &&
                    <li> <i className="icon icon-clock is-inline"></i>{details.date_cloture}</li> }
                  </ul>
                  {this.renderDetail()}
                  { details.featured_pdf && details.featured_pdf !== '' &&
                  <a 
                    href={details.featured_pdf} 
                    title="Télécharger l’offre en PDF" 
                    className="btn btn--white"
                    onClick={handleTagEvent(
                      'telechargement_document',
                      documentName,
                      '',
                      userPref
                    )}>
                    <i className="icon icon-download is-inline"></i><span>Télécharger l’offre en PDF</span>
                  </a> }
                  <JobDetailForm formId={formId} jobname={`${details.structure.name} - ${details.title}`} jobemail={details.email} onSubmit={(formInputs) => onSubmit({ ...formInputs, 'postuler-id': id }, formId)}/>
                </div>
              </div>
              </section>
            </div>
          </div>
        </div>
      </Fragment>
    );
  }
}

export default withHelmet(JobDetail)((Helmet, pageProps) => {
  const ogimage = pageProps.featured_media ? pageProps.featured_media.src : false;
  const ogimagetitle = pageProps.featured_media ? pageProps.featured_media.title : false;
  return (
    <Helmet>
      <title>{pageProps.meta_title}</title>
      <meta name={'description'} content={pageProps.meta_description} />
      <meta property={'og:url'} content={window.location.href} />
      <meta property={'og:title'} content={pageProps.meta_title} />
      <meta property={'og:description'} content={pageProps.meta_description} />
      {ogimage && <meta property={'og:image'} content={ogimage} />}
      {ogimage && <meta property={'og:image:url'} content={ogimage} />}
      {(ogimage && ogimagetitle) && <meta property={'og:image:title'} content={ogimagetitle} />}
    </Helmet>
  );
});
